import { useMemo } from 'react';
import Modal from '../../../components/modal/modal';
import Card from '../../../components/card/Card';
import P from '../../../components/texts/p/P';
import H from '../../../components/texts/h/H';
import Span from '../../../components/texts/span/Span';
import Icon from '../../../components/icon/Icon';
import { getErrorCopy } from '../../../utils/bulkErrorMessages';
import { useFiscalIdConfig } from '../../../hooks/useFiscalIdConfig';
import type {
  BulkAction,
  BulkDecisionResponse,
  BulkDecisionItemSuccess,
  BulkDecisionItemFailure,
  BulkErrorCode,
} from '../../../services/ApplicationService';

const MAX_RETRY_COUNT = 3;

const TOOLTIP_NO_RETRYABLE =
  'Ningún error es recuperable automáticamente.';

const TOOLTIP_MAX_RETRIES =
  'Alcanzaste el máximo de reintentos. Refrescá el listado.';

export interface CommitteeBulkResultModalProps {
  /** Visibilidad del modal — el padre la controla. */
  isOpen: boolean;
  /**
   * Resultado completo del bulk (`succeeded` + `failed`) tal como llega del
   * BFF (`POST /v1/private/committee/bulk-decision`). Ver tipos en
   * `src/services/ApplicationService.tsx`.
   */
  result: BulkDecisionResponse;
  /** Acción bulk ejecutada — adapta títulos y detalle de la sección de éxitos. */
  action: BulkAction;
  /**
   * Cantidad de reintentos ya realizados sobre este resultado. `0` en el
   * primer render. Phase 8 lo incrementa en el dashboard antes de pasar el
   * resultado actualizado de cada vuelta.
   *
   * Tope: cuando alcanza `MAX_RETRY_COUNT` (=3), el botón "Reintentar
   * fallidas (X)" queda deshabilitado con mensaje explicativo.
   */
  retryCount: number;
  /** Cierre — el padre decide qué hacer (limpiar selección, refrescar, etc.). */
  onClose: () => void;
  /**
   * Click en "Reintentar fallidas (X)" — Phase 8 filtra los items
   * recuperables, vuelve al estado `processing` y reusa la action +
   * description originales.
   */
  onRetryFailed: () => void;
}

/**
 * CommitteeBulkResultModal — modal de resultado del aprobador masivo de
 * comité. Cierra el ciclo de vida de una operación bulk mostrando qué
 * carpetas terminaron OK y cuáles fallaron, agrupadas por código de error
 * para que el usuario pueda decidir reintentar o continuar.
 *
 * REGLAS DE UX (OQ-4 cerrada con opción B — Engram #60 v2):
 *
 *   1. Sección "Aprobadas (N)" se renderiza sólo si `succeeded.length > 0`.
 *      Cada item muestra el identificador fiscal (formateado), `companyName` si
 *      vino, y un indicador del estado final calculado por el backend:
 *      "Aprobada", "Pasó a instrumentación", "Pasó a finalizada" o
 *      "Pendiente del próximo step".
 *
 *   2. Sección "No procesadas (M)" se subdivide en dos sub-secciones:
 *      - "Errores recuperables (X)": items con
 *        `getErrorCopy(errorCode).isRetryable === true` (LOCK_TIMEOUT,
 *        UPSTREAM_ERROR, UNKNOWN). Reintentar es lo correcto.
 *      - "Errores no recuperables (M-X)": items con `isRetryable === false`
 *        (STATUS_NOT_COM, FORBIDDEN_ROLE, NOT_AUTHORIZED_FOR_STEP,
 *        VALIDATION_ERROR). El usuario debe tomar otra acción.
 *
 *   3. Dentro de cada sub-sección, los items se agrupan por `errorCode` para
 *      no repetir el mismo título (ej. "Conflicto de concurrencia (3)" + 3
 *      identificadores fiscales debajo en lugar de 3 cards repetidos).
 *
 *   4. Botón "Reintentar fallidas (X)" del footer:
 *      - Habilitado SOLO si `X >= 1 && retryCount < 3`.
 *      - Si `X === 0`, queda deshabilitado y se renderiza una helper line
 *        "Ningún error es recuperable automáticamente." sobre el botón.
 *      - Si `retryCount >= 3`, queda deshabilitado y se renderiza una
 *        helper line "Alcanzaste el máximo de reintentos. Refrescá el
 *        listado.".
 *
 *   5. "Cerrar" del footer cierra el modal sin más side effects (Phase 8
 *      limpia estado y refresca el listado en su `cleanup` step).
 *
 *   6. Scroll: el cuerpo del modal se envuelve en un contenedor con
 *      `max-height: 60vh; overflow-y: auto` — replica el patrón de
 *      `ApprovalHistory.tsx` cuando la lista crece (>10 items combinados).
 *
 *   7. Sin hex hardcoded: todos los íconos / textos / estados usan los
 *      `variant` del sistema (`success`, `danger`, `neutral`, `warning`)
 *      para que el branding por cliente herede automáticamente del
 *      `ThemeContext`.
 *
 * NOTA SOBRE EL TOOLTIP:
 * El user task pedía un tooltip nativo (HTML `title`) en el botón
 * "Reintentar fallidas". El componente `Modal` del repo no expone
 * `primaryActionTitle` / passthrough de `title` al Button del footer. Para
 * no modificar el `Modal` compartido (regla project-standards y design
 * Engram #59 sección A.1: "Reuso (sin tocar): `Modal`"), se reemplaza el
 * tooltip por una helper line explícita arriba del footer (ítem 4 arriba).
 * UX equivalente o mejor: el motivo está siempre visible, no requiere hover.
 *
 * Linaje SDD: Engram #57 (explore v2) + #58 (proposal) + #59 (design D5) +
 * #60 (tasks v2 OQ-4 cerrada opción B) + #70 (apply-phase-6 foundation:
 * `BulkDecisionResponse` + `getErrorCopy`).
 */
const CommitteeBulkResultModal: React.FC<CommitteeBulkResultModalProps> = ({
  isOpen,
  result,
  action,
  retryCount,
  onClose,
  onRetryFailed,
}) => {
  const { succeeded, failed } = result;

  const retryableFailed = useMemo(
    () => failed.filter((item) => getErrorCopy(item.errorCode).isRetryable),
    [failed],
  );
  const nonRetryableFailed = useMemo(
    () => failed.filter((item) => !getErrorCopy(item.errorCode).isRetryable),
    [failed],
  );

  const retryableCount = retryableFailed.length;
  const retriesLeft = MAX_RETRY_COUNT - retryCount;
  const canRetry = retryableCount >= 1 && retriesLeft > 0;
  const retryDisabledReason = !canRetry
    ? retryableCount === 0
      ? TOOLTIP_NO_RETRYABLE
      : TOOLTIP_MAX_RETRIES
    : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title='Resultado del aprobador masivo'
      width='720px'
      primaryText={`Reintentar fallidas (${retryableCount})`}
      primaryVariant='primary'
      primaryActionDisabled={!canRetry}
      onPrimaryAction={onRetryFailed}
      secondaryText='Cerrar'
      secondaryVariant='secondary'
      onSecondaryAction={onClose}
    >
      <Card>
        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            width: '100%',
          }}
        >
          {succeeded.length > 0 && (
            <SucceededSection items={succeeded} action={action} />
          )}
          {failed.length > 0 && (
            <NoProcesadasSection
              total={failed.length}
              retryableItems={retryableFailed}
              nonRetryableItems={nonRetryableFailed}
            />
          )}
          {succeeded.length === 0 && failed.length === 0 && (
            <P type='1' variant='neutral' withMargin={false}>
              No hay carpetas en el resultado.
            </P>
          )}
        </div>
        {retryDisabledReason && (
          <Card margin={{ top: '3' }}>
            <P
              type='2'
              variant='neutral'
              modifier='weak'
              withMargin={false}
              align='right'
            >
              {retryDisabledReason}
            </P>
          </Card>
        )}
        {canRetry && retryCount > 0 && (
          <Card margin={{ top: '3' }}>
            <P
              type='2'
              variant='neutral'
              modifier='weak'
              withMargin={false}
              align='right'
            >
              Reintento {retryCount} de {MAX_RETRY_COUNT - 1}.
            </P>
          </Card>
        )}
      </Card>
    </Modal>
  );
};

export default CommitteeBulkResultModal;

const getSuccessTitle = (action: BulkAction, count: number): string => {
  switch (action) {
    case 'APPROVE':
      return `Aprobadas (${count})`;
    case 'REQUEST_CHANGES':
      return `Cambios solicitados (${count})`;
    case 'REJECT':
      return `Rechazadas (${count})`;
  }
};

/**
 * Sub-componente: sección de éxitos. Lista cada item con identificador fiscal formateado,
 * razón social opcional y, solo para APPROVE, un sub-status del BFF.
 */
const SucceededSection: React.FC<{
  items: BulkDecisionItemSuccess[];
  action: BulkAction;
}> = ({ items, action }) => (
  <Card margin={{ bottom: '4' }} display='grid' gap='8px'>
    <Card display='flex' alignItems='center' gap='8px'>
      <Icon name='check_circle_outline' variant='success' width={20} height={20} />
      <H type='5' variant='neutral' weight='bold' withMargin={false}>
        {getSuccessTitle(action, items.length)}
      </H>
    </Card>
    <Card display='grid' gap='4px'>
      {items.map((item) => (
        <SucceededItemRow key={`succ-${item.id}`} item={item} action={action} />
      ))}
    </Card>
  </Card>
);

const SucceededItemRow: React.FC<{
  item: BulkDecisionItemSuccess;
  action: BulkAction;
}> = ({ item, action }) => {
  const fiscalId = useFiscalIdConfig();
  return (
  <Card
    display='flex'
    alignItems='center'
    gap='12px'
    flexWrap='wrap'
    data-testid={`bulk-result-succeeded-${item.id}`}
  >
    <Span type='4' variant='neutral' weight='bold'>
      {fiscalId.format(item.cuit)}
    </Span>
    {item.companyName && (
      <Span type='4' variant='neutral'>
        {item.companyName}
      </Span>
    )}
    {action === 'APPROVE' && (
      <>
        <Span type='4' variant='neutral' modifier='weak'>
          —
        </Span>
        <Span type='4' variant='success' weight='bold'>
          {describeSuccess(item)}
        </Span>
      </>
    )}
  </Card>
  );
};

/**
 * Texto descriptivo del estado final de una carpeta exitosa. Refleja la
 * decisión que tomó ms-application (LEGACY o MEMBER_QUORUM) tras aplicar
 * la acción.
 */
const describeSuccess = (item: BulkDecisionItemSuccess): string => {
  if (item.transitionedToInstrumentation) {
    return 'Pasó a instrumentación';
  }
  if (item.finalStatusCode === 'FIN' || item.finalStatusCode === 'APP_FINAL') {
    return 'Pasó a finalizada';
  }
  if (item.finalStatusCode === 'APP') {
    return 'Pendiente del próximo step';
  }
  if (item.finalStatusCode === 'REJ') {
    return 'Declinada';
  }
  if (item.finalStatusCode === 'PEN') {
    return 'Cambios solicitados';
  }
  return 'Aprobada';
};

/**
 * Sub-componente: sección "No procesadas (M)". Subdivide en
 * recuperables / no recuperables, agrupando dentro de cada sub-sección
 * por `errorCode` para no repetir títulos.
 */
const NoProcesadasSection: React.FC<{
  total: number;
  retryableItems: BulkDecisionItemFailure[];
  nonRetryableItems: BulkDecisionItemFailure[];
}> = ({ total, retryableItems, nonRetryableItems }) => (
  <Card margin={{ bottom: '2' }} display='grid' gap='12px'>
    <Card display='flex' alignItems='center' gap='8px'>
      <Icon name='warning_triangle_outline' variant='danger' width={20} height={20} />
      <H type='5' variant='neutral' weight='bold' withMargin={false}>
        No procesadas ({total})
      </H>
    </Card>
    {retryableItems.length > 0 && (
      <FailedSubsection
        title={`Errores recuperables (${retryableItems.length})`}
        items={retryableItems}
        variant='warning'
        testIdPrefix='bulk-result-retryable'
      />
    )}
    {nonRetryableItems.length > 0 && (
      <FailedSubsection
        title={`Errores no recuperables (${nonRetryableItems.length})`}
        items={nonRetryableItems}
        variant='danger'
        testIdPrefix='bulk-result-non-retryable'
      />
    )}
  </Card>
);

const FailedSubsection: React.FC<{
  title: string;
  items: BulkDecisionItemFailure[];
  variant: 'warning' | 'danger';
  testIdPrefix: string;
}> = ({ title, items, variant, testIdPrefix }) => {
  const grouped = useMemo(() => groupByErrorCode(items), [items]);
  const fiscalId = useFiscalIdConfig();
  return (
    <Card margin={{ left: '2' }} display='grid' gap='8px'>
      <P type='2' variant={variant} weight='bold' withMargin={false}>
        {title}
      </P>
      {grouped.map(({ errorCode, items: groupItems }) => {
        const copy = getErrorCopy(errorCode);
        const displayMessage =
          groupItems.map((item) => item.message?.trim()).find(Boolean) ??
          copy.message;
        return (
          <Card
            key={`${testIdPrefix}-group-${errorCode}`}
            margin={{ left: '2' }}
            display='grid'
            gap='4px'
            data-testid={`${testIdPrefix}-group-${errorCode}`}
          >
            <P type='3' variant='neutral' weight='bold' withMargin={false}>
              {copy.title} ({groupItems.length})
            </P>
            <P type='4' variant='neutral' modifier='weak' withMargin={false}>
              {displayMessage}
            </P>
            <Card display='grid' gap='2px' margin={{ left: '2', top: '1' }}>
              {groupItems.map((item) => (
                <Card
                  key={`${testIdPrefix}-${item.id}`}
                  display='flex'
                  alignItems='center'
                  gap='8px'
                  flexWrap='wrap'
                  data-testid={`${testIdPrefix}-item-${item.id}`}
                >
                  <Span type='4' variant='neutral' weight='bold'>
                    {fiscalId.format(item.cuit)}
                  </Span>
                  {item.companyName && (
                    <Span type='4' variant='neutral'>
                      {item.companyName}
                    </Span>
                  )}
                </Card>
              ))}
            </Card>
          </Card>
        );
      })}
    </Card>
  );
};

/**
 * Agrupa items de fallo por `errorCode` preservando el orden de aparición
 * para que la UI sea estable entre renders / reintentos.
 */
const groupByErrorCode = (
  items: BulkDecisionItemFailure[],
): Array<{ errorCode: BulkErrorCode; items: BulkDecisionItemFailure[] }> => {
  const order: BulkErrorCode[] = [];
  const buckets = new Map<BulkErrorCode, BulkDecisionItemFailure[]>();
  items.forEach((item) => {
    if (!buckets.has(item.errorCode)) {
      buckets.set(item.errorCode, []);
      order.push(item.errorCode);
    }
    buckets.get(item.errorCode)!.push(item);
  });
  return order.map((errorCode) => ({
    errorCode,
    items: buckets.get(errorCode)!,
  }));
};
